<script setup>
import { useI18n } from 'vue-i18n';

import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import Label from 'dashboard/components-next/label/Label.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

defineProps({
  templates: { type: Array, required: true },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['delete']);

const { t } = useI18n();

// Meta's real status values are lowercase in some Cloud API responses and
// uppercase in others depending on endpoint/version - this codebase's own
// existing convention (TemplateStep.vue, WhatsAppTemplateParser.vue,
// Whatsapp::TemplateProcessorService#find_template) already normalizes via
// case-insensitive comparison, so the library does the same rather than
// assuming one casing.
const statusColor = status => {
  switch (status?.toLowerCase()) {
    case 'approved':
      return 'teal';
    case 'pending':
      return 'amber';
    case 'rejected':
      return 'ruby';
    default:
      return 'slate';
  }
};

const bodyPreview = template => {
  const body = template.components?.find(c => c.type === 'BODY');
  return body?.text || '';
};

const componentSummary = template => {
  const types = (template.components || []).map(c => c.type);
  return types.join(', ');
};

const canDelete = template => !!template.name;
</script>

<template>
  <div v-if="isLoading" class="flex items-center justify-center py-10">
    <Spinner />
  </div>

  <BaseTable
    v-else
    :headers="[
      t('WHATSAPP_TEMPLATES.LIBRARY.TABLE.NAME'),
      t('WHATSAPP_TEMPLATES.LIBRARY.TABLE.LANGUAGE'),
      t('WHATSAPP_TEMPLATES.LIBRARY.TABLE.CATEGORY'),
      t('WHATSAPP_TEMPLATES.LIBRARY.TABLE.STATUS'),
      t('WHATSAPP_TEMPLATES.LIBRARY.TABLE.BODY_PREVIEW'),
      t('WHATSAPP_TEMPLATES.LIBRARY.TABLE.COMPONENTS'),
      t('WHATSAPP_TEMPLATES.LIBRARY.TABLE.ACTIONS'),
    ]"
    :items="templates"
    :no-data-message="t('WHATSAPP_TEMPLATES.LIBRARY.TABLE.NO_TEMPLATES')"
  >
    <template #row="{ items }">
      <BaseTableRow
        v-for="template in items"
        :key="`${template.name}-${template.language}`"
        :item="template"
      >
        <template #default>
          <BaseTableCell class="font-medium">{{ template.name }}</BaseTableCell>
          <BaseTableCell>{{ template.language }}</BaseTableCell>
          <BaseTableCell class="capitalize">
            {{ (template.category || '').toLowerCase() }}
          </BaseTableCell>
          <BaseTableCell>
            <Label
              :label="template.status"
              :color="statusColor(template.status)"
              compact
              class="capitalize"
            />
          </BaseTableCell>
          <BaseTableCell
            class="max-w-xs truncate"
            :title="bodyPreview(template)"
          >
            {{ bodyPreview(template) }}
          </BaseTableCell>
          <BaseTableCell class="text-xs text-n-slate-11">
            {{ componentSummary(template) }}
          </BaseTableCell>
          <BaseTableCell>
            <Button
              v-if="canDelete(template)"
              variant="ghost"
              size="sm"
              icon="i-lucide-trash-2"
              :aria-label="t('WHATSAPP_TEMPLATES.LIBRARY.TABLE.DELETE')"
              @click="emit('delete', template)"
            />
          </BaseTableCell>
        </template>
      </BaseTableRow>
    </template>
  </BaseTable>
</template>
